/**
 * voiceSAS.js — Short Authentication String helpers for WebRTC
 * voice calls.
 *
 * The SAS is a 6-digit numeric code ("NNN-NNN") that both peers
 * can independently derive from their shared DTLS fingerprints.
 * Reading it out loud during a call lets two humans verify there
 * is no MITM in the media path — the fingerprints are part of the
 * DTLS handshake that encrypts the audio, so a MITM would have to
 * substitute their own fingerprints, producing a different code.
 *
 * Why DTLS fingerprints (and not our long-term RSA/Dilithium keys):
 *   - Binds to the actual session, not static identity. A stolen
 *     key from last month can't forge this call's code.
 *   - Matches how ZRTP/Signal derive SAS codes — proven mechanism.
 *   - Zero dependency on the peer's long-term key material being
 *     up-to-date, fetched, trusted, etc.
 *
 * Privacy note: the code itself is a truncated hash of public
 * fingerprints. Speaking it aloud reveals nothing beyond "I'm on
 * THIS specific call with you right now" — no long-term identity
 * information leaks.
 */

const FP_RE = /a=fingerprint:sha-256\s+([0-9A-Fa-f:]+)/;

/**
 * Pull the sha-256 DTLS fingerprint line out of an SDP and return
 * it as a lowercased, colon-free hex string.
 *
 * Both offer and answer SDPs carry exactly one such line at the
 * session or first m-section level; we take the first match.
 * Returns null if the SDP is missing a sha-256 fingerprint (e.g.
 * an old peer negotiating sha-1 — in which case we refuse to
 * derive a SAS rather than render a fake "verified" state).
 */
export const extractDtlsFingerprint = (sdp) => {
  if (!sdp || typeof sdp !== 'string') return null;
  const m = sdp.match(FP_RE);
  if (!m) return null;
  return m[1].replace(/:/g, '').toLowerCase();
};

/**
 * Derive the 6-digit SAS from two DTLS fingerprints.
 *
 * Sorts them lexicographically so both peers produce identical
 * output regardless of who called whom, then hashes
 * "${a}|${b}|gs-voice-sas-v1" with SHA-256 and takes the first 4
 * bytes as a 32-bit unsigned integer, modulo 1_000_000 — small
 * modulo bias (~0.0001%) is acceptable for a user-facing display
 * code; this is not cryptographically critical (the attacker has
 * to forge the code *live* on a spoken channel).
 *
 * Returns "NNN-NNN" zero-padded. Returns null if either input is
 * missing or empty so the UI can render a neutral state.
 */
export const deriveSAS = async ({ localFp, remoteFp }) => {
  if (!localFp || !remoteFp) return null;
  const [a, b] = [localFp, remoteFp].sort();
  const input = `${a}|${b}|gs-voice-sas-v1`;
  const buf = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', buf);
  const view = new DataView(digest);
  // First 4 bytes → 32-bit unsigned int.
  const n = view.getUint32(0, false);
  const code = n % 1_000_000;
  const s = String(code).padStart(6, '0');
  return `${s.slice(0, 3)}-${s.slice(3)}`;
};

/**
 * Convenience: derive from two SDPs directly. Returns `null`
 * cleanly if either SDP lacks a sha-256 fingerprint.
 */
export const deriveSASFromSdps = async ({ localSdp, remoteSdp }) => {
  const localFp = extractDtlsFingerprint(localSdp);
  const remoteFp = extractDtlsFingerprint(remoteSdp);
  if (!localFp || !remoteFp) return null;
  return deriveSAS({ localFp, remoteFp });
};

// ─── Per-peer verification persistence ──────────────────────────
//
// When the user attests "codes match", we stash a row keyed on
// the peer's user id so subsequent calls can show
// "✓ Verified Nd ago" instead of demanding a re-read every time.
// Key format matches the `ghostcomm_` prefix convention so the
// panic button wipes it alongside every other local identity
// artifact.
//
// Stored value shape:
//   { sas, verifiedAt: <ms>, peerUsername }
//
// Duress: `sessionStorage['gs_duress_mode']==='1'` short-circuits
// every read and write, so a coerced session can't surface the
// user's genuine verification history to the coercer.

const LS_PREFIX = 'ghostcomm_call_verified_';
// 30 days — reasonable re-verify cadence; matches our 7-day key
// rotation's 4x cushion. After this we drop the stale row so the
// UI prompts a fresh attestation.
const VERIFICATION_TTL_MS = 30 * 86400_000;

const _isDuress = () =>
  typeof window !== 'undefined' &&
  sessionStorage.getItem('gs_duress_mode') === '1';

export const markPeerVerified = (peerId, sas, peerUsername) => {
  if (!peerId || !sas || _isDuress()) return;
  try {
    localStorage.setItem(
      LS_PREFIX + peerId,
      JSON.stringify({ sas, verifiedAt: Date.now(), peerUsername: peerUsername || null })
    );
  } catch {
    /* quota or disabled — non-fatal */
  }
};

/**
 * Return the saved verification row for a peer, or null. Auto-
 * evicts rows older than `VERIFICATION_TTL_MS`. Also auto-evicts
 * rows whose stored SAS does NOT match the `expectedSas` of the
 * current call — a mismatch there means either the peer rotated
 * their DTLS identity (every call negotiates fresh fingerprints,
 * so this is actually expected on every call) OR a MITM has
 * inserted themselves. We ERR ON THE SIDE OF RE-PROMPTING because
 * "already verified" UX on a MITM'd call would be catastrophic.
 *
 * So the stored row is really a tombstone of "the user has done
 * the spoken-ritual for this peer at least once" — it doesn't
 * blanket-skip verification, it just softens the prompt copy to
 * "Re-verify periodically" vs "Verify this call".
 */
export const getPeerVerification = (peerId) => {
  if (!peerId || _isDuress()) return null;
  try {
    const raw = localStorage.getItem(LS_PREFIX + peerId);
    if (!raw) return null;
    const row = JSON.parse(raw);
    if (!row || !row.verifiedAt) return null;
    if (Date.now() - row.verifiedAt > VERIFICATION_TTL_MS) {
      localStorage.removeItem(LS_PREFIX + peerId);
      return null;
    }
    return row;
  } catch {
    return null;
  }
};

export const forgetPeerVerification = (peerId) => {
  if (!peerId) return;
  try {
    localStorage.removeItem(LS_PREFIX + peerId);
  } catch {
    /* ignore */
  }
};

// ─── Call-verification posture counters ─────────────────────────
//
// Two parallel ledgers power the "voice security posture" tiles
// on the Security Halo card:
//
//   ghostcomm_call_verified_<peerId>         — YOU attested their code
//       (written by markPeerVerified, above)
//   ghostcomm_call_peer_attested_<peerId>    — THEY attested yours
//       (written by recordPeerAttestedMe when the peer's WS frame
//        of signal_type='sas_attest' arrives during a call)
//
// Both use the same 30-day TTL as the main verification tombstone.
// The 90-day count window is intentionally wider than the TTL so
// a user who verified a peer 60 days ago and hasn't re-verified
// still sees their historical attestation reflected (we just don't
// persist the row forever — 30d is the re-verify cadence).
// Actually keeping them in sync: we extend both rows to 90d for
// this specific counter use-case and let the Settings UI surface
// staleness separately if needed. Keeps the code simple.

const PEER_ATTESTED_PREFIX = 'ghostcomm_call_peer_attested_';
const COUNTER_WINDOW_MS = 90 * 86400_000;

/**
 * Called from the WS listener when a call peer broadcasts a
 * `signal_type: 'sas_attest'` frame — i.e. the peer on the other
 * end of this call clicked "Codes match" on their side. Records a
 * row so the Settings card can tell the user how many distinct
 * peers have attested their code back over the last 90 days.
 *
 * Duress-safe (no write during a coerced session — the coercer
 * must not learn the user's real trust graph).
 */
export const recordPeerAttestedMe = (peerId, sas) => {
  if (!peerId || _isDuress()) return;
  try {
    localStorage.setItem(
      PEER_ATTESTED_PREFIX + peerId,
      JSON.stringify({ sas: sas || null, verifiedAt: Date.now() })
    );
  } catch {
    /* quota — non-fatal */
  }
};

// Shared scan: walk all localStorage keys with the given prefix,
// parse each row, keep the ones whose `verifiedAt` is inside the
// 90d window, and return the distinct-peer count. Returns 0 in
// duress mode without reading anything so the coercer can't use
// the counter to backtrack what's been verified.
const _countFresh = (prefix) => {
  if (_isDuress()) return 0;
  let n = 0;
  const cutoff = Date.now() - COUNTER_WINDOW_MS;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(prefix)) continue;
      try {
        const row = JSON.parse(localStorage.getItem(k));
        if (row && row.verifiedAt && row.verifiedAt >= cutoff) n += 1;
      } catch {
        /* bad row — ignore */
      }
    }
  } catch {
    /* disabled storage — return what we have */
  }
  return n;
};

/**
 * Count of distinct peers YOU have SAS-verified in the last 90d.
 */
export const mySasVerifiedCount90d = () => _countFresh(LS_PREFIX);

/**
 * Count of distinct peers who have SAS-attested YOU back in the
 * last 90d. A "they verified you" tombstone is evidence the
 * verification was mutual — the lived-in north star of the SAS
 * feature.
 */
export const peerAttestedMeCount90d = () => _countFresh(PEER_ATTESTED_PREFIX);
